import { useEffect, useState } from "react";
import Button from "@/components/Button/Button";
import Modal from "@/components/Modal/Modal";
import styles from "./CancelPlanModal.module.scss";

export type CancelPlanOption = "now" | "next_month";

interface CancelPlanModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (option: CancelPlanOption) => void;
  isSubmitting?: boolean;
}

const CANCEL_OPTIONS: Array<{
  value: CancelPlanOption;
  title: string;
  description: string;
}> = [
  {
    value: "now",
    title: "Cancel Now",
    description: "Cancel immediately.",
  },
  {
    value: "next_month",
    title: "Cancel Next Month",
    description:
      "Continue with your current plan for now. Your membership will be cancelled on next billing date.",
  },
];

const CancelPlanModal = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
}: CancelPlanModalProps) => {
  const [selectedOption, setSelectedOption] = useState<CancelPlanOption | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedOption(null);
    }
  }, [open]);

  const handleConfirm = () => {
    if (!selectedOption) return;
    onConfirm(selectedOption);
  };

  return (
    <Modal
      bottomSheetOnMobile
      bodyClassName={styles.modalBody}
      className={styles.modalShell}
      closeModal={() => onClose()}
      heading={
        <div className={styles.headingBlock}>
          <div className={styles.handle} />
          <h2 className={styles.title}>When would you like to cancel?</h2>
        </div>
      }
      modalOpen={open}
    >
      <div className={styles.options}>
        {CANCEL_OPTIONS.map((option) => {
          const isSelected = selectedOption === option.value;

          return (
            <button
              className={`${styles.optionCard} ${isSelected ? styles.optionCardSelected : ""}`}
              key={option.value}
              onClick={() => setSelectedOption(option.value)}
              type="button"
            >
              <span className={`${styles.radio} ${isSelected ? styles.radioSelected : ""}`} />
              <span className={styles.optionCopy}>
                <strong>{option.title}</strong>
                <span>{option.description}</span>
              </span>
            </button>
          );
        })}
      </div>

      <Button
        className={styles.confirmButton}
        disabled={!selectedOption || isSubmitting}
        onClick={handleConfirm}
        type="button"
      >
        {isSubmitting ? "Cancelling..." : "Cancel"}
      </Button>
    </Modal>
  );
};

export default CancelPlanModal;
